// Демо-данные окна «Рабочие процессы»: категории шаблонов, сами шаблоны со схемой шагов
// и готовые процессы демо-режима.

import { t } from "@/lib/i18n";

export type WorkflowStepKind = "trigger" | "message" | "team" | "task" | "condition" | "delay";

export type WorkflowStep = {
  id: string;
  kind: WorkflowStepKind;
  title: string;
  /** подпись строки внутри узла: «Сообщение:», «Выберите команду», «Условие:» */
  label?: string;
  /** текст-превью внутри узла */
  text?: string;
  /** показывать как поле выбора (рамка + стрелка) */
  field?: boolean;
  /** подпись на соединительной линии над узлом */
  connector?: string;
};

export type WorkflowTemplate = {
  id: string;
  name: string;
  category: string;
  isNew?: boolean;
  summary: string;
  intro: string;
  benefits: string[];
  how: string[];
  steps: WorkflowStep[];
};

export type WorkflowStatus = "active" | "draft" | "paused";

export type WorkflowRecord = {
  id: string;
  name: string;
  status: WorkflowStatus;
  template: string | null;
  steps: WorkflowStep[];
  author: string;
  updated: string;
};

export const WORKFLOW_STEP_LIMIT = 100;

export const flowchartCategories = [
  { id: "all", label: t("Все шаблоны"), icon: "ri-layout-grid-line" },
  { id: "autoreply", label: t("Автоответчик"), icon: "ri-reply-all-line" },
  { id: "routing", label: t("Маршрутизация И Назначение"), icon: "ri-route-line" },
  { id: "business", label: t("Бизнес-Процессы"), icon: "ri-flow-chart" },
  { id: "supervision", label: t("Надзор И Отчетность"), icon: "ri-bar-chart-box-line" },
  { id: "meta", label: t("Мета-Реклама «Нажмите в WhatsApp»"), icon: "ri-meta-line" },
  { id: "messenger", label: t("Реклама В Мессенджерах"), icon: "ri-megaphone-line" },
];

export const workflowStatuses: { id: WorkflowStatus; label: string; dot: string; pill: string }[] = [
  { id: "active", label: t("Активен"), dot: "bg-emerald-500", pill: "bg-emerald-500/10 text-emerald-700" },
  { id: "draft", label: t("Черновик"), dot: "bg-foreground/30", pill: "bg-foreground/[.06] text-foreground/70" },
  { id: "paused", label: t("Приостановлен"), dot: "bg-amber-500", pill: "bg-amber-500/10 text-amber-700" },
];

export const workflowTriggers = [
  { id: "conversation", label: t("Разговор начался") },
  { id: "contact", label: t("Новый контакт в рабочем пространстве") },
  { id: "call", label: t("Звонок завершён") },
  { id: "message", label: t("Получено входящее сообщение") },
];

export const workflowEmpty = {
  title: t("У вас пока нет рабочего процесса."),
  text: t("Используйте рабочие процессы для автоматизации процессов в маркетинге, продажах, поддержке, уведомлениях и многом другом."),
  scratch: t("Начать с нуля"),
  templates: t("Начните с шаблона"),
};

export const workflowHints = [
  {
    icon: "ri-flashlight-line",
    title: t("Что такое рабочий процесс"),
    text: t("Это цепочка действий: триггер запускает её, а дальше автоматически уходят сообщения, назначаются агенты и создаются задания."),
  },
  {
    icon: "ri-route-line",
    title: t("С чего начать"),
    text: t("Возьмите готовый шаблон из галереи — он уже собран из рабочих шагов. Или начните с нуля и добавьте триггер вручную."),
  },
  {
    icon: "ri-list-check-2",
    title: t("Что такое шаг"),
    text: t("Один шаг — одно действие процесса. В процессе может быть до 100 шагов, а схема наглядно показывает порядок и ветки."),
  },
];

export const workflowTemplatesHint = t("Шаблоны уже собраны из проверенных шагов: выберите подходящий, переименуйте под свою задачу — и процесс можно включать.");

export const workflowTemplates: WorkflowTemplate[] = [
  {
    id: "call-summary",
    name: t("Сводка звонков по журналу"),
    category: "supervision",
    isNew: true,
    summary: t("После каждого завершения звонка автоматически записывайте структурированное резюме разговора в карточку контакта."),
    intro:
      t("Каждый звонок заканчивается одинаково: менеджер дописывает итог в CRM, а половина деталей теряется. Этот процесс сам записывает резюме разговора — тему, договорённости и следующие шаги — и складывает его в карточку контакта."),
    benefits: [
      t("Менеджеры не тратят время на заметки после звонка."),
      t("Руководитель видит, о чём именно договорились с клиентом."),
      t("История разговоров остаётся в карточке даже после смены менеджера."),
    ],
    how: [
      t("Контакт или менеджер завершает звонок."),
      t("Процесс получает журнал звонка и расшифровку разговора."),
      t("Резюме сохраняется в поле контакта «Последний звонок»."),
      t("Менеджеру уходит сообщение с кратким итогом."),
    ],
    steps: [
      { id: "cs-1", kind: "trigger", title: t("Курок"), text: t("Звонок завершён") },
      { id: "cs-2", kind: "message", title: t("Расшифровать звонок"), label: t("Действие:"), text: t("Получить журнал разговора и ключевые темы.") },
      { id: "cs-3", kind: "task", title: t("Записать резюме"), label: t("Поле:"), text: t("Последний звонок"), connector: t("Успех") },
      { id: "cs-4", kind: "message", title: t("Сообщение менеджеру"), label: t("Сообщение:"), text: t("Итог звонка с $contact.name: $call.summary.") },
    ],
  },
  {
    id: "missed-call",
    name: t("Задание: Отслеживание пропущенного звонка"),
    category: "business",
    summary: t("Автоматически создавайте задание на перезвон, когда клиент звонит и не дожидается ответа."),
    intro:
      t("Пропущенный звонок — это почти потерянная сделка. Процесс проверяет, ответил ли кто-то клиенту, и если нет — ставит задание ответственному с именем контакта и временем звонка."),
    benefits: [
      t("Ни один пропущенный звонок не остаётся без перезвона."),
      t("Задание сразу попадает к ответственному агенту, а не в общую очередь."),
      t("Руководитель видит, сколько звонков команда пропустила за неделю."),
    ],
    how: [
      t("Клиент звонит и завершает вызов, не дождавшись ответа."),
      t("Процесс проверяет длительность ожидания."),
      t("Контакт назначается ответственному агенту."),
      t("Агент получает задание перезвонить в течение 15 минут."),
    ],
    steps: [
      { id: "mc-1", kind: "trigger", title: t("Курок"), text: t("Пропущенный звонок") },
      { id: "mc-2", kind: "condition", title: t("Проверить ожидание"), label: t("Условие:"), text: t("Ответа не было дольше 30 секунд") },
      { id: "mc-3", kind: "team", title: t("Назначить ответственного"), label: t("Выберите команду"), field: true },
      { id: "mc-4", kind: "task", title: t("Задание на перезвон"), label: t("Сообщение:"), text: t("Перезвоните $contact.name — пропущенный звонок в $call.time."), connector: t("Успех") },
    ],
  },
  {
    id: "least-open-contacts",
    name: t("Задание: Наименее открытые контакты"),
    category: "routing",
    summary: t("Назначайте контакт агенту с наименьшим количеством открытых диалогов — так ответ приходит быстрее."),
    intro:
      t("Когда ваши контакты впервые обращаются в вашу компанию, вы можете автоматически назначать их агентам с наименьшим количеством открытых контактов. Агенты, не перегруженные открытыми контактами, с большей вероятностью смогут быстрее ответить вашим клиентам."),
    benefits: [
      t("Убедитесь, что все контакты оперативно переданы агентам."),
      t("Экономьте время и повышайте точность данных по сравнению с процессами ручного назначения."),
      t("Улучшение времени отклика и повышение удовлетворённости клиентов."),
    ],
    how: [
      t("Контакт инициирует разговор, отправив сообщение."),
      t("Отправьте ответное сообщение контакту."),
      t("Назначьте контакт агенту, у которого наименьшее количество открытых контактов."),
      t("Отправьте сообщение, чтобы уведомить контактное лицо о назначении агента."),
    ],
    steps: [
      { id: "lc-1", kind: "trigger", title: t("Курок"), text: t("Разговор начался") },
      {
        id: "lc-2",
        kind: "message",
        title: t("Приветственное сообщение"),
        label: t("Сообщение:"),
        text: t("Здравствуйте, $contact.name, я ищу сотрудника в команду, которому можно передать диалог."),
      },
      { id: "lc-3", kind: "team", title: t("Назначить в команду…"), label: t("Выберите команду"), field: true },
      {
        id: "lc-4",
        kind: "task",
        title: t("Сообщение о задании"),
        label: t("Сообщение:"),
        text: t("Вам назначен исполнитель $assignee.firstname."),
        connector: t("Успех"),
      },
    ],
  },
  {
    id: "round-robin",
    name: t("Задание: Круговой турнир"),
    category: "routing",
    summary: t("Назначайте контакт члену команды поочерёдно каждый раз, когда контакт начинает разговор."),
    intro:
      t("Круговое распределение убирает ручную работу: каждый новый диалог по очереди уходит следующему агенту команды, и никто не остаётся без нагрузки или, наоборот, с перегрузом."),
    benefits: [
      t("Нагрузка распределяется равномерно, без споров «кто взял клиента»."),
      t("Ни один агент не пропускает свою очередь."),
      t("Процесс легко приостановить в отпуск или на время обучения."),
    ],
    how: [
      t("Контакт начинает разговор."),
      t("Процесс берёт следующего агента из очереди команды."),
      t("Контакт назначается этому агенту."),
      t("Агент получает сообщение с именем контакта."),
    ],
    steps: [
      { id: "rr-1", kind: "trigger", title: t("Курок"), text: t("Разговор начался") },
      { id: "rr-2", kind: "message", title: t("Приветствие"), label: t("Сообщение:"), text: t("Спасибо за сообщение! Сейчас подключится наш специалист.") },
      { id: "rr-3", kind: "team", title: t("Назначить по очереди"), label: t("Выберите команду"), field: true },
      { id: "rr-4", kind: "task", title: t("Уведомить исполнителя"), label: t("Сообщение:"), text: t("Новый диалог: $contact.name."), connector: t("Успех") },
    ],
  },
  {
    id: "round-robin-online",
    name: t("Задание: Круговой турнир + Только онлайн-формат"),
    category: "routing",
    summary: t("Круговое назначение только для агентов онлайн: офлайн-коллеги пропускаются до конца смены."),
    intro:
      t("Если половина команды офлайн, круговое распределение теряет смысл. Процесс проверяет статус агентов и передаёт контакт только тому, кто сейчас на линии."),
    benefits: [
      t("Клиент получает ответ от того, кто действительно на месте."),
      t("Офлайн-агенты не получают просроченные задания."),
      t("Процесс сам переключается, когда коллега выходит на линию."),
    ],
    how: [
      t("Контакт начинает разговор."),
      t("Процесс проверяет, кто из команды сейчас онлайн."),
      t("Контакт назначается следующему онлайн-агенту."),
      t("Агент получает задание взять диалог."),
    ],
    steps: [
      { id: "ro-1", kind: "trigger", title: t("Курок"), text: t("Разговор начался") },
      { id: "ro-2", kind: "condition", title: t("Только онлайн"), label: t("Условие:"), text: t("Статус агента — «на линии»") },
      { id: "ro-3", kind: "team", title: t("Назначить по кругу"), label: t("Выберите команду"), field: true },
      { id: "ro-4", kind: "task", title: t("Задание агенту"), label: t("Сообщение:"), text: t("Возьмите диалог: $contact.name."), connector: t("Успех") },
    ],
  },
  {
    id: "cancel-assignment",
    name: t("Задание: Отменить назначение после завершения обсуждения"),
    category: "routing",
    summary: t("Снимайте назначение, если обсуждение завершилось раньше, чем агент успел ответить."),
    intro:
      t("Иногда клиент закрывает вопрос сам или пишет в другой канал. Процесс проверяет, активно ли ещё назначение, и возвращает контакт в очередь команды, чтобы агент не тратил время на закрытый диалог."),
    benefits: [
      t("Агенты не работают с уже закрытыми диалогами."),
      t("Контакты возвращаются в очередь без ручных действий."),
      t("В отчётах не остаётся «зависших» назначений."),
    ],
    how: [
      t("Обсуждение завершается."),
      t("Процесс проверяет, активно ли назначение."),
      t("Если агент ещё не ответил — назначение снимается."),
      t("Контакт возвращается в очередь команды."),
    ],
    steps: [
      { id: "ca-1", kind: "trigger", title: t("Курок"), text: t("Обсуждение завершено") },
      { id: "ca-2", kind: "condition", title: t("Назначение ещё активно?"), label: t("Условие:"), text: t("Агент не ответил") },
      { id: "ca-3", kind: "message", title: t("Снять назначение"), label: t("Действие:"), text: t("Контакт возвращён в очередь команды.") },
    ],
  },
  {
    id: "out-of-office",
    name: t("Сообщение об отсутствии"),
    category: "autoreply",
    summary: t("Отправляйте сообщение об отсутствии каждый раз, когда контакт начинает разговор вне смены."),
    intro:
      t("Клиент пишет вечером и не получает ответа — впечатление испорчено. Процесс отвечает сразу, честно говорит, когда вернётся команда, и сохраняет диалог открытым до утра."),
    benefits: [
      t("Клиент получает ответ в ту же минуту, даже ночью."),
      t("Команда не просыпается от сообщений в нерабочее время."),
      t("Диалог остаётся в очереди и не теряется."),
    ],
    how: [
      t("Контакт начинает разговор."),
      t("Процесс проверяет, есть ли кто-то из команды на линии."),
      t("Если все офлайн — уходит сообщение об отсутствии."),
      t("Диалог остаётся открытым до начала смены."),
    ],
    steps: [
      { id: "oo-1", kind: "trigger", title: t("Курок"), text: t("Разговор начался") },
      { id: "oo-2", kind: "condition", title: t("Все офлайн?"), label: t("Условие:"), text: t("Нет агентов на линии") },
      {
        id: "oo-3",
        kind: "message",
        title: t("Сообщение об отсутствии"),
        label: t("Сообщение:"),
        text: t("Спасибо за сообщение! Мы ответим, как только вернёмся на линию."),
        connector: t("Успех"),
      },
    ],
  },
  {
    id: "out-of-office-hours",
    name: t("Сообщение об отсутствии с указанием рабочего времени"),
    category: "autoreply",
    summary: t("Отправляйте сообщение об отсутствии с точными часами работы команды."),
    intro:
      t("Клиенту важно понимать, когда ждать ответ. Процесс проверяет рабочее время и отправляет сообщение с часами работы — без общих формулировок «мы скоро ответим»."),
    benefits: [
      t("Клиент сразу понимает, когда получит ответ."),
      t("Меньше повторных сообщений «вы тут?»."),
      t("Часы работы меняются в одном месте, а не в каждом шаблоне."),
    ],
    how: [
      t("Контакт начинает разговор."),
      t("Процесс проверяет рабочее время команды."),
      t("Вне рабочих часов уходит сообщение с часами работы."),
      t("В рабочие часы диалог сразу идёт к агенту."),
    ],
    steps: [
      { id: "oh-1", kind: "trigger", title: t("Курок"), text: t("Разговор начался") },
      { id: "oh-2", kind: "condition", title: t("Проверить рабочее время"), label: t("Условие:"), text: t("Вне 09:00–19:00") },
      {
        id: "oh-3",
        kind: "message",
        title: t("Сообщение с часами работы"),
        label: t("Сообщение:"),
        text: t("Мы работаем по будням с 09:00 до 19:00 и ответим в ближайшее рабочее время."),
      },
    ],
  },
  {
    id: "meta-click-whatsapp",
    name: t("Мета-Реклама «Нажмите в WhatsApp»"),
    category: "meta",
    summary: t("Принимайте клики по рекламе в WhatsApp, приветствуйте контакт и сразу передавайте его продавцу."),
    intro:
      t("Клиент нажимает кнопку в объявлении и попадает в WhatsApp. Процесс мгновенно отвечает, фиксирует источник «Мета-реклама» и передаёт диалог продавцу, пока интерес не остыл."),
    benefits: [
      t("Клиент получает ответ в первые секунды после клика."),
      t("В карточке контакта видно, из какого объявления пришёл лид."),
      t("Продавец получает тёплый контакт без ручной пересылки."),
    ],
    how: [
      t("Клиент нажимает кнопку в рекламном объявлении."),
      t("Открывается диалог WhatsApp с приветствием."),
      t("Источник сохраняется в поле контакта."),
      t("Диалог назначается продавцу команды."),
    ],
    steps: [
      { id: "mcw-1", kind: "trigger", title: t("Курок"), text: t("Клик по рекламе") },
      { id: "mcw-2", kind: "message", title: t("Приветствие из рекламы"), label: t("Сообщение:"), text: t("Здравствуйте! Вы писали по объявлению — расскажите, что ищете.") },
      { id: "mcw-3", kind: "team", title: t("Назначить продавцу"), label: t("Выберите команду"), field: true },
    ],
  },
  {
    id: "messenger-ad-reply",
    name: t("Реклама В Мессенджерах: квалификация лида"),
    category: "messenger",
    summary: t("Отвечайте на рекламу в мессенджерах и квалифицируйте контакт до передачи продавцу."),
    intro:
      t("Реклама приводит много любопытных, но мало готовых к покупке. Процесс задаёт два квалифицирующих вопроса и передаёт продавцу только тех, кто назвал задачу и бюджет."),
    benefits: [
      t("Продавцы тратят время только на целевые диалоги."),
      t("Клиент получает ответ сразу после клика по объявлению."),
      t("Ответы квалификации сохраняются в полях контакта."),
    ],
    how: [
      t("Клиент отвечает на рекламу в мессенджере."),
      t("Процесс задаёт вопросы о задаче и сроках."),
      t("Ответы сохраняются в карточке контакта."),
      t("Целевой контакт назначается менеджеру."),
    ],
    steps: [
      { id: "ma-1", kind: "trigger", title: t("Курок"), text: t("Сообщение из рекламы") },
      { id: "ma-2", kind: "message", title: t("Квалификация"), label: t("Сообщение:"), text: t("Подскажите задачу и желаемый срок — подберём решение.") },
      { id: "ma-3", kind: "condition", title: t("Готов к покупке?"), label: t("Условие:"), text: t("Бюджет и срок указаны") },
      { id: "ma-4", kind: "team", title: t("Назначить менеджеру"), label: t("Выберите команду"), field: true },
    ],
  },
];

// ── готовые процессы демо-режима ──

export const demoWorkflows: WorkflowRecord[] = [
  {
    id: "wf-1",
    name: t("Приветствие новых лидов"),
    status: "active",
    template: "least-open-contacts",
    author: t("Мария Петрова"),
    updated: "2026-10-27",
    steps: [
      { id: "wf1-1", kind: "trigger", title: t("Курок"), text: t("Разговор начался") },
      { id: "wf1-2", kind: "message", title: t("Приветствие"), label: t("Сообщение:"), text: t("Здравствуйте, $contact.name! Сейчас подключится специалист.") },
      { id: "wf1-3", kind: "team", title: t("Назначить в команду…"), label: t("Выберите команду"), field: true },
      { id: "wf1-4", kind: "task", title: t("Сообщение о задании"), label: t("Сообщение:"), text: t("Вам назначен исполнитель $assignee.firstname."), connector: t("Успех") },
    ],
  },
  {
    id: "wf-2",
    name: t("Сводка звонков отдела продаж"),
    status: "active",
    template: "call-summary",
    author: t("Алексей Ким"),
    updated: "2026-10-24",
    steps: [
      { id: "wf2-1", kind: "trigger", title: t("Курок"), text: t("Звонок завершён") },
      { id: "wf2-2", kind: "message", title: t("Расшифровать звонок"), label: t("Действие:"), text: t("Получить журнал разговора и ключевые темы.") },
      { id: "wf2-3", kind: "task", title: t("Записать резюме"), label: t("Поле:"), text: t("Последний звонок"), connector: t("Успех") },
    ],
  },
  {
    id: "wf-3",
    name: t("Ночной автоответчик"),
    status: "paused",
    template: "out-of-office-hours",
    author: t("Ольга Смирнова"),
    updated: "2026-10-19",
    steps: [
      { id: "wf3-1", kind: "trigger", title: t("Курок"), text: t("Разговор начался") },
      { id: "wf3-2", kind: "condition", title: t("Проверить рабочее время"), label: t("Условие:"), text: t("Вне 09:00–19:00") },
      { id: "wf3-3", kind: "message", title: t("Сообщение с часами работы"), label: t("Сообщение:"), text: t("Мы работаем по будням с 09:00 до 19:00.") },
    ],
  },
  {
    id: "wf-4",
    name: t("Пропущенный звонок — перезвон"),
    status: "draft",
    template: "missed-call",
    author: t("Дмитрий Волков"),
    updated: "2026-10-28",
    steps: [
      { id: "wf4-1", kind: "trigger", title: t("Курок"), text: t("Пропущенный звонок") },
      { id: "wf4-2", kind: "condition", title: t("Проверить ожидание"), label: t("Условие:"), text: t("Ответа не было дольше 30 секунд") },
      { id: "wf4-3", kind: "task", title: t("Задание на перезвон"), label: t("Сообщение:"), text: t("Перезвоните $contact.name — пропущенный звонок.") },
    ],
  },
];